import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserSegmentScope } from '../../auth/hooks/useUserSegmentScope';
import { useCustomerSegmentOptions } from '../../../hooks/options/useCustomerSegmentOptions';
import { useUrlListFilter } from '../../../lib/query/useUrlListFilter';
import { KANBAN_FILTER_FIELDS } from '../../../routes/filterMaps';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { Alert, Card, Col, Form, Modal, Row, Select, Space, Tag, Typography } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useLeadsKanban } from '../hooks/useLeadsKanban';
import { useChangeLeadStage } from '../hooks/useChangeLeadStage';
import { useLeadPipeline } from '../hooks/useLeadPipeline';
import { useCampaignOptions } from '../../../hooks/options/useCampaignOptions';
import type { LeadKanbanItem } from '../types';
import type { LeadPipelineStageDefinition } from '../types';
import type { ClosingStatus } from '../types';
import { getBantTier, PIPELINE_STAGE_CODE_TO_STAGE, isClosedCategory } from '../constants';
import { useLeadSources } from '../hooks/useLeadSources';
import { useContactCategories } from '../../contacts/hooks/useContactCategories';
import ContactSearchSelect from '../../contacts/components/ContactSearchSelect';
import PageScaffold from '../../../components/PageScaffold';
import { FilterBar } from '../../../components/list';
import FeedbackState from '../../../components/FeedbackState';
import CloseLeadModal, { type CloseLeadFormValues } from './CloseLeadModal';
import { buildCloseWonSchedulePayload } from '../utils/closeWonSchedulePayload';
import { ApiError } from '../../../lib/api/error';

import { defaultFollowUpTitle } from '../utils/followUpTitle';
import ScheduleNextFollowupFields from '../../tasks/components/ScheduleNextFollowupFields';

dayjs.extend(utc);

const STAGE_DROP_PREFIX = 'stage-';
const LEAD_DRAG_PREFIX = 'lead-';

function getStageIdFromDropId(dropId: string): string | null {
  if (!dropId.startsWith(STAGE_DROP_PREFIX)) return null;
  const stageId = dropId.slice(STAGE_DROP_PREFIX.length);
  return stageId ? stageId : null;
}

function DroppableColumn({
  stageId,
  children,
  minHeight,
}: {
  stageId: string;
  children: React.ReactNode;
  minHeight: string | number;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: `${STAGE_DROP_PREFIX}${stageId}` });
  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight,
        borderRadius: 8,
        backgroundColor: isOver ? 'rgba(0,0,0,0.04)' : undefined,
        transition: 'background-color 0.2s',
      }}
    >
      {children}
    </div>
  );
}

function DraggableCard({
  lead,
  onClick,
  sourceLabelById,
}: {
  lead: LeadKanbanItem;
  onClick: () => void;
  sourceLabelById: Record<number, string>;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${LEAD_DRAG_PREFIX}${lead.id}`,
    data: { lead },
  });
  return (
    <div ref={setNodeRef} style={{ marginBottom: 8 }}>
      <Card
        size="small"
        type="inner"
        hoverable
        className="cp-hover-card"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        style={{
          cursor: 'pointer',
          opacity: isDragging ? 0.5 : 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div
            {...listeners}
            {...attributes}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              padding: '2px 4px',
              margin: '-2px -4px',
              color: 'var(--ant-color-text-tertiary)',
              touchAction: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="Drag to move"
          >
            <HolderOutlined style={{ fontSize: 14 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography.Text strong>{lead.title}</Typography.Text>
            <div style={{ marginTop: 6, fontSize: 13, fontWeight: 500 }}>
              {lead.targetedAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </div>
            <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Tag color={getBantTier(lead.bantTotalScore).color}>
                BANT {getBantTier(lead.bantTotalScore).label} ({lead.bantTotalScore})
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {sourceLabelById[lead.sourceId] ?? '-'}
                {lead.campaignName ? ` · ${lead.campaignName}` : ''}
              </Typography.Text>
              {lead.estimatedClosingDate && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Est. {dayjs(lead.estimatedClosingDate).format('MMM D')}
                </Typography.Text>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export interface LeadsKanbanPageProps {
  onLeadClick?: (leadId: string) => void;
}

const LeadsKanbanPage: React.FC<LeadsKanbanPageProps> = ({ onLeadClick }) => {
  const { isScoped, assignedSegmentIds, defaultSegmentIdsForApi } = useUserSegmentScope();
  const { data: segments = [] } = useCustomerSegmentOptions({ activeOnly: true });
  interface KanbanFilterState {
    sourceFilter?: number;
    campaignFilter?: string;
    contactCategoryFilter?: number;
    contactFilters: string[];
    segmentFilter: string[];
  }

  const defaultKanbanFilter: KanbanFilterState = useMemo(() => ({
    sourceFilter: undefined,
    campaignFilter: undefined,
    contactCategoryFilter: undefined,
    contactFilters: [],
    segmentFilter: [],
  }), []);

  const [kanbanFilters, setKanbanFilters, , silentSetKanbanFilters] = useUrlListFilter(defaultKanbanFilter, KANBAN_FILTER_FIELDS);
  const { sourceFilter, campaignFilter, contactCategoryFilter, contactFilters, segmentFilter } = kanbanFilters;

  const patchKanbanFilter = useCallback((patch: Partial<KanbanFilterState>) => {
    setKanbanFilters((prev) => ({ ...prev, ...patch }));
  }, [setKanbanFilters]);

  useEffect(() => {
    if (isScoped) {
      silentSetKanbanFilters((prev: KanbanFilterState) => ({
        ...prev,
        segmentFilter: prev.segmentFilter.length > 0 ? prev.segmentFilter : defaultSegmentIdsForApi(),
      }));
    }
  }, [isScoped, assignedSegmentIds.join(','), defaultSegmentIdsForApi, silentSetKanbanFilters]);
  const {
    data: pipeline,
    isError: isPipelineError,
    error: pipelineError,
  } = useLeadPipeline();
  const { data: sources } = useLeadSources();
  const { data: categories } = useContactCategories();

  const sourceLabelById = React.useMemo(() => {
    const m: Record<number, string> = {};
    (sources ?? []).forEach(s => { m[s.id] = s.label; });
    return m;
  }, [sources]);
  const sourceOptions = sources?.filter(s => s.isActive).map(s => ({ value: s.id, label: s.label })) ?? [];
  const categoryOptions = categories?.filter(c => c.isActive).map(c => ({ value: c.id, label: c.label })) ?? [];

  const segmentFilterOptions = useMemo(() => {
    if (!isScoped) {
      return [];
    }
    const allowed = new Set(assignedSegmentIds);
    return segments
      .filter((s) => allowed.has(s.id))
      .map((s) => ({ value: s.id, label: s.label }));
  }, [isScoped, assignedSegmentIds, segments]);

  const kanbanParams = {
    sourceId: sourceFilter,
    campaignId: campaignFilter,
    contactCategoryId: contactCategoryFilter,
    contactIds: contactFilters,
    segmentIds: defaultSegmentIdsForApi(segmentFilter),
  };
  const {
    data: leads = [],
    isLoading,
    isError: isLeadsError,
    error: leadsError,
  } = useLeadsKanban(kanbanParams);
  const { data: campaigns = [] } = useCampaignOptions();
  const changeStageMutation = useChangeLeadStage();
  const [activeLead, setActiveLead] = useState<LeadKanbanItem | null>(null);
  const dragJustEndedRef = useRef(false);

  const stages = React.useMemo(() => {
    const raw = pipeline?.stages ?? [];
    return [...raw]
      .filter((s) => s.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [pipeline?.stages]);

  const stageById = React.useMemo(() => {
    const map: Record<string, LeadPipelineStageDefinition> = {};
    for (const s of stages) map[String(s.id)] = s;
    return map;
  }, [stages]);

  const stageIdByLegacyStage = React.useMemo(() => {
    const map: Record<number, string> = {};
    for (const s of stages) {
      const legacy = PIPELINE_STAGE_CODE_TO_STAGE[s.code];
      if (legacy && !map[legacy]) {
        map[legacy] = String(s.id);
      }
    }
    return map;
  }, [stages]);

  const leadsByStage = React.useMemo(() => {
    const map: Record<string, LeadKanbanItem[]> = {};
    for (const s of stages) map[String(s.id)] = [];
    map.__unknown__ = [];
    for (const lead of leads) {
      const pipelineStageIdKey = String(lead.pipelineStageId);
      const stageId = map[pipelineStageIdKey]
        ? pipelineStageIdKey
        : stageIdByLegacyStage[lead.pipelineStage as unknown as number];
      const key = stageId && map[stageId] ? stageId : '__unknown__';
      map[key].push(lead);
    }
    return map;
  }, [leads, stages, stageIdByLegacyStage]);

  const [closeForm] = Form.useForm<CloseLeadFormValues>();

  const [closeFlow, setCloseFlow] = useState<null | { leadId: string; toStageCode: string; closingStatus: ClosingStatus }>(null);

  const [openStageForm] = Form.useForm();

  const [openStageFlow, setOpenStageFlow] = useState<null | {
    leadId: string;
    toStageCode: string;
    leadTitle: string;
    stageLabel: string;
  }>(null);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    dragJustEndedRef.current = false;
    const id = String(event.active.id);
    if (id.startsWith(LEAD_DRAG_PREFIX)) {
      const leadId = id.slice(LEAD_DRAG_PREFIX.length);
      const lead = leads.find((l) => l.id === leadId);
      if (lead) setActiveLead(lead);
    }
  }, [leads]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveLead(null);
      dragJustEndedRef.current = true;
      setTimeout(() => {
        dragJustEndedRef.current = false;
      }, 150);
      const { active, over } = event;
      if (!over) return;
      const activeId = String(active.id);
      const overId = String(over.id);
      if (!activeId.startsWith(LEAD_DRAG_PREFIX) || !overId.startsWith(STAGE_DROP_PREFIX)) return;
      const leadId = activeId.slice(LEAD_DRAG_PREFIX.length);
      const toStageId = getStageIdFromDropId(overId);
      if (toStageId === null) return;
      const lead = leads.find((l) => l.id === leadId);
      if (!lead || String(lead.pipelineStageId) === toStageId) return;

      const stage = stageById[toStageId];
      if (!stage) return;

      if (isClosedCategory(stage.category) && stage.closingStatus != null) {
        setCloseFlow({ leadId, toStageCode: stage.code, closingStatus: stage.closingStatus });
        closeForm.resetFields();
        closeForm.setFieldsValue({
          scheduleNext: true,
          nextTitle: defaultFollowUpTitle(lead.title),
        });
        return;
      }

      setOpenStageFlow({ leadId, toStageCode: stage.code, leadTitle: lead.title, stageLabel: stage.label });
      openStageForm.resetFields();
    },
    [leads, changeStageMutation, stageById, closeForm, openStageForm]
  );

  const handleDragCancel = useCallback(() => {
    setActiveLead(null);
    dragJustEndedRef.current = true;
    setTimeout(() => {
      dragJustEndedRef.current = false;
    }, 150);
  }, []);

  const handleCardClick = useCallback(
    (leadId: string) => {
      if (dragJustEndedRef.current) return;
      onLeadClick?.(leadId);
    },
    [onLeadClick]
  );

  const campaignOptions = campaigns.map((c) => ({ value: c.id, label: c.label }));

  const handleCloseOk = () => {
    if (!closeFlow) return;
    closeForm.validateFields().then((values) => {
      const schedulePayload = buildCloseWonSchedulePayload(values);

      changeStageMutation.mutate(
        {
          leadId: closeFlow.leadId,
          toStageId: closeFlow.toStageCode,
          closedAmount: values.closedAmount,
          actualClosingDate: values.actualClosingDate?.utc(true).startOf('day').toISOString(),
          closingNote: values.closingNote,
          ...schedulePayload,
        },
        {
          onSuccess: () => {
            setCloseFlow(null);
            closeForm.resetFields();
          },
        }
      );
    });
  };

  const handleOpenStageOk = () => {
    if (!openStageFlow) return;
    openStageForm.validateFields().then((values) => {
      const schedulePayload = buildCloseWonSchedulePayload(values);
      changeStageMutation.mutate(
        {
          leadId: openStageFlow.leadId,
          toStageId: openStageFlow.toStageCode,
          ...schedulePayload,
        },
        {
          onSuccess: () => {
            setOpenStageFlow(null);
            openStageForm.resetFields();
          },
        }
      );
    });
  };

  return (
    <>
      <PageScaffold
        title="Pipeline"
        subtitle="Drag leads between stages. Filters narrow the board without changing stage definitions."
        filters={(
          <FilterBar
            onReset={
              sourceFilter !== undefined ||
              campaignFilter !== undefined ||
              contactCategoryFilter !== undefined ||
              contactFilters.length > 0 ||
              segmentFilter.length > 0
                ? () => patchKanbanFilter({
                    sourceFilter: undefined,
                    campaignFilter: undefined,
                    contactCategoryFilter: undefined,
                    contactFilters: [],
                    segmentFilter: [],
                  })
                : undefined
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Source</span>
              <Select<number>
                allowClear
                options={sourceOptions}
                value={sourceFilter}
                onChange={(v) => patchKanbanFilter({ sourceFilter: v })}
                style={{ width: 140 }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Campaign</span>
              <Select<string>
                allowClear
                options={campaignOptions}
                value={campaignFilter}
                onChange={(v) => patchKanbanFilter({ campaignFilter: v })}
                style={{ width: 180 }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Contact Category</span>
              <Select<number>
                allowClear
                options={categoryOptions}
                value={contactCategoryFilter}
                onChange={(v) => patchKanbanFilter({ contactCategoryFilter: v })}
                style={{ width: 180 }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Contact</span>
              <ContactSearchSelect
                value={contactFilters}
                onChange={(v) => patchKanbanFilter({ contactFilters: v })}
                style={{ minWidth: 220 }}
              />
            </div>
            {segmentFilterOptions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Customer Segments</span>
                <Select<string[]>
                  mode="multiple"
                  allowClear
                  options={segmentFilterOptions}
                  value={segmentFilter}
                  onChange={(v) => patchKanbanFilter({ segmentFilter: v })}
                  style={{ minWidth: 200 }}
                  optionFilterProp="label"
                  maxTagCount="responsive"
                />
              </div>
            ) : null}
          </FilterBar>
        )}
      >
      {isLoading && leads.length === 0 ? (
        <FeedbackState loading />
      ) : isPipelineError ? (
        <Alert
          type="error"
          showIcon
          message={ApiError.fromAxiosError(pipelineError).message}
        />
      ) : isLeadsError ? (
        <Alert
          type="error"
          showIcon
          message={ApiError.fromAxiosError(leadsError).message}
        />
      ) : leads.length === 0 ? (
        <FeedbackState isEmpty emptyDescription="No leads match the selected filters." />
      ) : (
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Row gutter={[16, 16]} wrap={false} style={{ display: 'flex', overflowX: 'auto', height: '100%' }}>
            {stages.map((stage) => (
              <Col key={stage.id} flex="0 0 280px">
                <Card
                  size="small"
                  title={
                    <Space>
                      <span>{stage.label}</span>
                      <Typography.Text type="secondary">
                        ({(leadsByStage[String(stage.id)] ?? []).length})
                      </Typography.Text>
                    </Space>
                  }
                  style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  styles={{ body: { flex: 1, overflowY: 'auto', padding: 8, minHeight: 0 } }}
                >
                  <DroppableColumn stageId={String(stage.id)} minHeight={'100%'}>
                    <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                      {(leadsByStage[String(stage.id)] ?? []).map((lead) => (
                        <DraggableCard
                          key={lead.id}
                          lead={lead}
                          onClick={() => handleCardClick(lead.id)}
                          sourceLabelById={sourceLabelById}
                        />
                      ))}
                    </Space>
                  </DroppableColumn>
                </Card>
              </Col>
            ))}
          </Row>
          </div>

          <DragOverlay>
            {activeLead ? (
              <Card size="small" type="inner" style={{ width: 264, cursor: 'grabbing' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <HolderOutlined style={{ fontSize: 14, color: 'var(--ant-color-text-tertiary)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography.Text strong>{activeLead.title}</Typography.Text>
                    <div style={{ marginTop: 6, fontSize: 13, fontWeight: 500 }}>
                      {activeLead.targetedAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <Tag color={getBantTier(activeLead.bantTotalScore).color}>
                        BANT {getBantTier(activeLead.bantTotalScore).label} ({activeLead.bantTotalScore})
                      </Tag>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {sourceLabelById[activeLead.sourceId] ?? '-'}
                        {activeLead.campaignName ? ` · ${activeLead.campaignName}` : ''}
                      </Typography.Text>
                      {activeLead.estimatedClosingDate && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Est. {dayjs(activeLead.estimatedClosingDate).format('MMM D')}
                        </Typography.Text>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
      </PageScaffold>

      <Modal
        title={`Move to ${openStageFlow?.stageLabel ?? ''}`}
        open={openStageFlow != null}
        onOk={handleOpenStageOk}
        onCancel={() => {
          setOpenStageFlow(null);
          openStageForm.resetFields();
        }}
        confirmLoading={changeStageMutation.isPending}
        destroyOnClose
      >
        <div className="cp-form-section">
          <Form form={openStageForm} layout="vertical">
            <ScheduleNextFollowupFields showPriority />
          </Form>
        </div>
      </Modal>

      <CloseLeadModal
        open={closeFlow != null}
        form={closeForm}
        fixedClosingStatus={closeFlow?.closingStatus}
        leadTitle={closeFlow ? leads.find((l) => l.id === closeFlow.leadId)?.title : undefined}
        onOk={handleCloseOk}
        onCancel={() => {
          setCloseFlow(null);
          closeForm.resetFields();
        }}
        confirmLoading={changeStageMutation.isPending}
      />
    </>
  );
};

export default LeadsKanbanPage;
